import { Navigate, useLocation } from "react-router-dom";
import { useAtomValue } from "jotai";
import { userAtom } from "@/store/atoms";
import { adminEmails } from "@/utils/format";
import { useUserRole } from "@/providers/RoleProvider";
import { ADMIN_PAGE_ID_MAP } from "@/constant/adminPages";

/**
 * When user visits /user or /user/dashboard: redirect to /user/dashboard only if
 * they have dashboard access; otherwise redirect to their first allowed page.
 */
export function UserDefaultRedirect() {
  const { hasPageAccess, getFirstAllowedUserPath } = useUserRole();
  const to = hasPageAccess("dashboard") ? "/user/dashboard" : getFirstAllowedUserPath();
  return <Navigate to={to} replace />;
}

/**
 * When admin visits /admin: redirect to /admin/dashboard only if they have
 * admin_dashboard access; otherwise redirect to their first allowed admin page.
 * If they have no admin pages, show an in-admin message (never redirect to /user).
 */
export function AdminDefaultRedirect() {
  const { hasPageAccess, getFirstAllowedAdminPath } = useUserRole();
  const firstAdmin = getFirstAllowedAdminPath();
  if (firstAdmin === "/admin") {
    return (
      <div className="flex flex-1 items-center justify-center p-8 text-center">
        <div className="rounded-lg border border-dark-300/30 bg-dark-200/50 px-6 py-8 max-w-md">
          <p className="text-white font-medium">No admin pages assigned</p>
          <p className="mt-2 text-sm text-gray-400">
            Your role does not have access to any admin pages. Please contact an administrator.
          </p>
        </div>
      </div>
    );
  }
  const to = hasPageAccess("admin_dashboard") ? "/admin/dashboard" : firstAdmin;
  return <Navigate to={to} replace />;
}

/**
 * Wraps the admin Outlet. If the current path maps to a role-protected pageId,
 * checks access. Super admins bypass. Otherwise redirects to first allowed admin page.
 */
export function AdminRouteGuard({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const user = useAtomValue(userAtom);
  const { hasPageAccess, getFirstAllowedAdminPath } = useUserRole();

  const hasSpecificRole = user?.roleId && user.roleId !== 1 && user.roleId != null && user.roleId !== undefined;
  const isSuperAdmin = Boolean(user?.email && adminEmails.includes(user.email) && !hasSpecificRole);

  if (isSuperAdmin) {
    return <>{children}</>;
  }

  const pathKey = location.pathname.replace(/^\//, "");
  const pageId = ADMIN_PAGE_ID_MAP[pathKey];

  if (!pageId) {
    return <>{children}</>;
  }

  if (!hasPageAccess(pageId)) {
    return <Navigate to={getFirstAllowedAdminPath()} state={{ from: location }} replace />;
  }

  return <>{children}</>;
}
